import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Fade,
  Tabs,
  Tab,
} from '@mui/material';

import MicIcon from '@mui/icons-material/Mic';
import ImageIcon from '@mui/icons-material/Image';
import TextIcon from '@mui/icons-material/Description';
import PlayIcon from '@mui/icons-material/PlayArrow';
import CheckIcon from '@mui/icons-material/CheckCircle';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AnimationIcon from '@mui/icons-material/Animation';
import TreeIcon from '@mui/icons-material/AccountTree';
import ChartIcon from '@mui/icons-material/ShowChart';
import InfoIcon from '@mui/icons-material/Info';
import UploadIcon from '@mui/icons-material/CloudUpload';
import RequestPageIcon from '@mui/icons-material/RequestPage';

import { styled } from '@mui/material/styles';

const DemoCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const UploadArea = styled(Paper)(({ theme, isDragging }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  borderRadius: theme.spacing(2),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`demo-tabpanel-${index}`}
      aria-labelledby={`demo-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const DiagnosticCentral = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [demoState, setDemoState] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState({
    acoustic: null,
    radiology: null,
    clinical: null,
  });
  const [attentionWeights, setAttentionWeights] = useState([0, 0, 0]);
  const [diagnosis, setDiagnosis] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [showVisualization, setShowVisualization] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [logs, setLogs] = useState([]);
  const [fusedEmbedding, setFusedEmbedding] = useState(null);
  const [diseaseDistances, setDiseaseDistances] = useState({
    COPD: 0,
    Pneumonia: 0,
    'Lung Cancer': 0,
  });
  const [xaiExplanation, setXaiExplanation] = useState('');

  const fileInputRefs = {
    acoustic: useRef(null),
    radiology: useRef(null),
    clinical: useRef(null),
  };

  const handleFileUpload = (modality, file) => {
    if (file) {
      setUploadedFiles(prev => ({
        ...prev,
        [modality]: file,
      }));
      addLog(`📁 Uploaded ${modality} file: ${file.name}`);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e, modality) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(modality, file);
    }
  };

  const handleFileInput = (e, modality) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(modality, file);
    }
  };

  const addLog = (message) => {
    setLogs(prev => [...prev, {
      timestamp: new Date().toLocaleTimeString(),
      message,
    }]);
  };

  const runDemo = async () => {
    setDemoState('processing');
    setProgress(0);
    setActiveStep(0);
    setLogs([]);
    setDiagnosis(null);
    setConfidence(0);
    setAttentionWeights([0, 0, 0]);
    setShowVisualization(false);
    setFusedEmbedding(null);

    addLog('🚀 Starting multimodal fusion demo...');

    await simulateStep(1, 'Extracting modality embeddings...', 20);
    addLog('✅ Acoustic: Wav2Vec2 → 128D embedding');
    addLog('✅ Radiology: ResNet50 → 128D embedding');
    addLog('✅ Clinical: ClinicalBERT → 128D embedding');
    setActiveStep(1);

    await simulateStep(2, 'Aligning to common semantic space...', 40);
    addLog('✅ All embeddings aligned to shared disease space');
    setActiveStep(2);

    await simulateStep(3, 'Computing attention weights...', 60);
    const weights = [0.40, 0.35, 0.25];
    setAttentionWeights(weights);
    addLog(`📊 Attention weights: Acoustic=${weights[0].toFixed(2)}, Radiology=${weights[1].toFixed(2)}, Clinical=${weights[2].toFixed(2)}`);
    setActiveStep(3);

    await simulateStep(4, 'Applying residual connections...', 75);
    addLog('🔄 F(x) + x: Preserving original information');
    setActiveStep(4);

    await simulateStep(5, 'Generating fused embedding...', 85);
    const embedding = Array.from({ length: 128 }, () => (Math.random() - 0.5) * 2);
    setFusedEmbedding(embedding);
    addLog('🧬 Fused embedding created (128D)');
    setActiveStep(5);

    await simulateStep(6, 'Predicting disease...', 95);
    const distances = {
      COPD: 0.94,
      Pneumonia: 0.12,
      'Lung Cancer': 0.08,
    };
    setDiseaseDistances(distances);
    
    const topDisease = Object.keys(distances).reduce((a, b) => distances[a] > distances[b] ? a : b);
    setDiagnosis(topDisease);
    setConfidence(distances[topDisease]);

    const explanation = generateXAI(weights, topDisease, distances[topDisease]);
    setXaiExplanation(explanation);

    addLog(`🎯 Diagnosis: ${topDisease} (${(distances[topDisease] * 100).toFixed(0)}% confidence)`);
    setActiveStep(6);
    setShowVisualization(true);

    setProgress(100);
    setDemoState('completed');
    addLog('✅ Fusion complete!');
  };

  const simulateStep = (step, message, targetProgress) => {
    return new Promise((resolve) => {
      addLog(`⏳ ${message}`);
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = Math.min(prev + 2, targetProgress);
          if (newProgress >= targetProgress) {
            clearInterval(interval);
            resolve();
            return targetProgress;
          }
          return newProgress;
        });
      }, 150);
    });
  };

  const generateXAI = (weights, diagnosis, confidence) => {
    const modalityNames = ['acoustic', 'radiology', 'clinical'];
    const maxWeight = Math.max(...weights);
    const maxIndex = weights.indexOf(maxWeight);
    const mostImportant = modalityNames[maxIndex];

    let reasoning = `The model diagnosed **${diagnosis}** with ${(confidence * 100).toFixed(0)}% confidence based on the following analysis:`;

    if (mostImportant === 'acoustic') {
      reasoning += `\n\n🔊 **Acoustic data was most important (${(weights[0] * 100).toFixed(0)}%)** - The patient's wheezing patterns strongly correlate with ${diagnosis}.`;
    } else if (mostImportant === 'radiology') {
      reasoning += `\n\n🖼️ **Radiology data was most important (${(weights[1] * 100).toFixed(0)}%)** - The chest X-ray showed characteristic signs of ${diagnosis}.`;
    } else {
      reasoning += `\n\n📝 **Clinical data was most important (${(weights[2] * 100).toFixed(0)}%)** - The patient's symptoms and history align with ${diagnosis}.`;
    }

    reasoning += `\n\nThe model also considered:`;
    if (weights[1] > 0.2) reasoning += `\n- 🖼️ Radiology findings (${(weights[1] * 100).toFixed(0)}% weight)`;
    if (weights[2] > 0.2) reasoning += `\n- 📝 Clinical notes (${(weights[2] * 100).toFixed(0)}% weight)`;

    reasoning += `\n\n💡 **Recommendation:** Based on the fusion results, a ${diagnosis} diagnosis is recommended. Consider confirming with additional tests.`;

    return reasoning;
  };

  const renderAttentionBar = () => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1'];
    const labels = ['Acoustic', 'Radiology', 'Clinical'];

    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        {attentionWeights.map((weight, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{labels[index]}</Typography>
              <Typography variant="body2" fontWeight="bold">
                {(weight * 100).toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={weight * 100}
              sx={{
                height: 20,
                borderRadius: 10,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: colors[index],
                  borderRadius: 10,
                  transition: 'width 1s ease',
                },
              }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const renderDiseaseDistances = () => {
    const diseases = Object.keys(diseaseDistances);

    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        {diseases.map((disease) => (
          <Box key={disease} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                {disease}
                {diagnosis === disease && ' 🎯'}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {(diseaseDistances[disease] * 100).toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={diseaseDistances[disease] * 100}
              sx={{
                height: 20,
                borderRadius: 10,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: diagnosis === disease ? '#4CAF50' : '#90A4AE',
                  borderRadius: 10,
                },
              }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const resetDemo = () => {
    setDemoState('idle');
    setProgress(0);
    setActiveStep(0);
    setLogs([]);
    setDiagnosis(null);
    setConfidence(0);
    setAttentionWeights([0, 0, 0]);
    setShowVisualization(false);
    setFusedEmbedding(null);
    setXaiExplanation('');
    setUploadedFiles({
      acoustic: null,
      radiology: null,
      clinical: null,
    });
    addLog('🔄 Demo reset');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header with Request Demo Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            🧠 Diagnostic Central
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Experience multimodal fusion with CTF Alignment + Attention + Residual connections in real-time
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<RequestPageIcon />}
          onClick={() => navigate('/demo-request')}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            boxShadow: 3,
            fontWeight: 'bold',
            '&:hover': { boxShadow: 6, transform: 'scale(1.02)' },
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
          }}
        >
          Request Demo
        </Button>
      </Box>
      <Divider sx={{ mb: 4 }} />

      <Grid container spacing={3}>
        {/* Left Column - Input & Controls */}
        <Grid item xs={12} md={5}>
          <DemoCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📤 Upload Modalities
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Upload sample data or use the built-in demo files
              </Typography>

              {/* Acoustic Upload */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <MicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Acoustic Data (Audio)
                </Typography>
                <UploadArea
                  isDragging={false}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, 'acoustic')}
                  onClick={() => fileInputRefs.acoustic.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRefs.acoustic}
                    onChange={(e) => handleFileInput(e, 'acoustic')}
                    accept="audio/*"
                    style={{ display: 'none' }}
                  />
                  {uploadedFiles.acoustic ? (
                    <Box>
                      <CheckIcon color="success" sx={{ fontSize: 40 }} />
                      <Typography variant="body2">{uploadedFiles.acoustic.name}</Typography>
                    </Box>
                  ) : (
                    <Box>
                      <UploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Drag & drop audio file or click to browse
                      </Typography>
                    </Box>
                  )}
                </UploadArea>
              </Box>

              {/* Radiology Upload */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <ImageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Radiology Data (X-Ray/CT)
                </Typography>
                <UploadArea
                  isDragging={false}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, 'radiology')}
                  onClick={() => fileInputRefs.radiology.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRefs.radiology}
                    onChange={(e) => handleFileInput(e, 'radiology')}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  {uploadedFiles.radiology ? (
                    <Box>
                      <CheckIcon color="success" sx={{ fontSize: 40 }} />
                      <Typography variant="body2">{uploadedFiles.radiology.name}</Typography>
                    </Box>
                  ) : (
                    <Box>
                      <UploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Drag & drop image or click to browse
                      </Typography>
                    </Box>
                  )}
                </UploadArea>
              </Box>

              {/* Clinical Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <TextIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Clinical Data (Text Notes)
                </Typography>
                <UploadArea
                  isDragging={false}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, 'clinical')}
                  onClick={() => fileInputRefs.clinical.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRefs.clinical}
                    onChange={(e) => handleFileInput(e, 'clinical')}
                    accept=".txt,.md,.json"
                    style={{ display: 'none' }}
                  />
                  {uploadedFiles.clinical ? (
                    <Box>
                      <CheckIcon color="success" sx={{ fontSize: 40 }} />
                      <Typography variant="body2">{uploadedFiles.clinical.name}</Typography>
                    </Box>
                  ) : (
                    <Box>
                      <UploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Drag & drop text file or click to browse
                      </Typography>
                    </Box>
                  )}
                </UploadArea>
              </Box>

              {/* Controls */}
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={runDemo}
                  disabled={demoState === 'processing'}
                  startIcon={<PlayIcon />}
                >
                  {demoState === 'processing' ? 'Processing...' : 'Run Fusion Demo'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetDemo}
                  disabled={demoState === 'processing'}
                >
                  Reset
                </Button>
              </Box>

              {/* Progress */}
              {demoState === 'processing' && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    {progress}% Complete
                  </Typography>
                </Box>
              )}
            </CardContent>
          </DemoCard>
        </Grid>

        {/* Right Column - Results & Visualization */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
                  <Tab label="📊 Results" />
                  <Tab label="🧬 Fusion Process" />
                  <Tab label="📝 Logs" />
                  <Tab label="🧠 Explanation (XAI)" />
                </Tabs>
              </Box>

              {/* Results Tab */}
              <TabPanel value={selectedTab} index={0}>
                {demoState === 'idle' ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <PsychologyIcon sx={{ fontSize: 80, color: 'text.secondary' }} />
                    <Typography variant="h6" color="text.secondary">
                      Upload modalities and click "Run Fusion Demo"
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The fusion process will analyze all three modalities and provide a diagnosis
                    </Typography>
                  </Box>
                ) : (
                  <Fade in={true} timeout={500}>
                    <Box>
                      {diagnosis && (
                        <Box sx={{ mb: 3, p: 3, bgcolor: 'success.light', borderRadius: 2 }}>
                          <Typography variant="h5" gutterBottom>
                            🎯 Diagnosis: {diagnosis}
                          </Typography>
                          <Typography variant="h6">
                            Confidence: {(confidence * 100).toFixed(0)}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={confidence * 100}
                            sx={{ height: 12, borderRadius: 6, mt: 1 }}
                          />
                        </Box>
                      )}

                      <Typography variant="h6" gutterBottom>
                        🔍 Modality Importance (Attention Weights)
                      </Typography>
                      {renderAttentionBar()}

                      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        📊 Disease Cluster Distances
                      </Typography>
                      {renderDiseaseDistances()}

                      {showVisualization && fusedEmbedding && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="h6" gutterBottom>
                            🧬 Fused Embedding (128D)
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {fusedEmbedding.slice(0, 20).map((val, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    width: 12,
                                    height: 30,
                                    bgcolor: val > 0 ? 'primary.main' : 'error.main',
                                    opacity: Math.abs(val),
                                    borderRadius: 1,
                                  }}
                                  title={`Index ${idx}: ${val.toFixed(3)}`}
                                />
                              ))}
                              <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                ... and {fusedEmbedding.length - 20} more dimensions
                              </Typography>
                            </Box>
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  </Fade>
                )}
              </TabPanel>

              {/* Fusion Process Tab */}
              <TabPanel value={selectedTab} index={1}>
                <Stepper activeStep={activeStep} orientation="vertical">
                  <Step>
                    <StepLabel>Step 1: Extract Modality Embeddings</StepLabel>
                    <StepContent>
                      <Typography variant="body2">
                        Convert raw data to 128D embeddings:
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip label="Acoustic → Wav2Vec2" size="small" sx={{ m: 0.5 }} />
                        <Chip label="Radiology → ResNet50" size="small" sx={{ m: 0.5 }} />
                        <Chip label="Clinical → ClinicalBERT" size="small" sx={{ m: 0.5 }} />
                      </Box>
                    </StepContent>
                  </Step>

                  <Step>
                    <StepLabel>Step 2: CTF Semantic Alignment</StepLabel>
                    <StepContent>
                      <Typography variant="body2">
                        Align all embeddings to the same latent disease space
                      </Typography>
                    </StepContent>
                  </Step>

                  <Step>
                    <StepLabel>Step 3: Attention (Weight by Importance)</StepLabel>
                    <StepContent>
                      <Typography variant="body2">
                        Calculate attention scores for each modality
                      </Typography>
                      {attentionWeights.some(w => w > 0) && (
                        <Box sx={{ mt: 1 }}>
                          <Chip label={`Acoustic: ${(attentionWeights[0]*100).toFixed(0)}%`} color="primary" size="small" sx={{ m: 0.5 }} />
                          <Chip label={`Radiology: ${(attentionWeights[1]*100).toFixed(0)}%`} color="secondary" size="small" sx={{ m: 0.5 }} />
                          <Chip label={`Clinical: ${(attentionWeights[2]*100).toFixed(0)}%`} color="info" size="small" sx={{ m: 0.5 }} />
                        </Box>
                      )}
                    </StepContent>
                  </Step>

                  <Step>
                    <StepLabel>Step 4: Residual (F(x) + x)</StepLabel>
                    <StepContent>
                      <Typography variant="body2">
                        Apply residual connections to preserve original information
                      </Typography>
                    </StepContent>
                  </Step>

                  <Step>
                    <StepLabel>Step 5: Fuse Embeddings</StepLabel>
                    <StepContent>
                      <Typography variant="body2">
                        Sum weighted residual outputs into a single fused embedding
                      </Typography>
                    </StepContent>
                  </Step>

                  <Step>
                    <StepLabel>Step 6: Predict Disease</StepLabel>
                    <StepContent>
                      <Typography variant="body2">
                        Find closest disease cluster in latent space
                      </Typography>
                      {diagnosis && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                          Diagnosis: {diagnosis} ({(confidence*100).toFixed(0)}% confidence)
                        </Alert>
                      )}
                    </StepContent>
                  </Step>
                </Stepper>
              </TabPanel>

              {/* Logs Tab */}
              <TabPanel value={selectedTab} index={2}>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {logs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No logs yet. Run the demo to see the fusion process.
                    </Typography>
                  ) : (
                    logs.map((log, idx) => (
                      <Box key={idx} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          [{log.timestamp}]
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {log.message}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </TabPanel>

              {/* XAI Tab */}
              <TabPanel value={selectedTab} index={3}>
                {xaiExplanation ? (
                  <Fade in={true}>
                    <Box>
                      <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {xaiExplanation}
                        </Typography>
                      </Paper>

                      <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Most Important Modality
                            </Typography>
                            <Typography variant="h6">
                              {attentionWeights[0] >= attentionWeights[1] && attentionWeights[0] >= attentionWeights[2] && '🔊 Acoustic'}
                              {attentionWeights[1] >= attentionWeights[0] && attentionWeights[1] >= attentionWeights[2] && '🖼️ Radiology'}
                              {attentionWeights[2] >= attentionWeights[0] && attentionWeights[2] >= attentionWeights[1] && '📝 Clinical'}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Confidence Level
                            </Typography>
                            <Typography variant="h6">
                              {(confidence * 100).toFixed(0)}%
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          💡 This explanation is generated using the model's attention weights and latent space distances.
                        </Typography>
                      </Box>
                    </Box>
                  </Fade>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <InfoIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Run the demo to see the AI explanation
                    </Typography>
                  </Box>
                )}
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Features Footer */}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TreeIcon color="primary" />
              <Typography variant="body2">
                <strong>CTF Alignment:</strong> All modalities mapped to same semantic space
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChartIcon color="secondary" />
              <Typography variant="body2">
                <strong>Attention:</strong> Modalities weighted by importance
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AnimationIcon color="success" />
              <Typography variant="body2">
                <strong>Residual:</strong> Preserves original information with F(x) + x
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default DiagnosticCentral;
